import { CARRIER_LOGISTICS } from 'shared/modules/carrier-profile/data/CarrierInfoDTO';
import { yupObject, yupString } from 'shared/utils/YupUtils';
import { Schema, StringSchema } from 'yup';
import { z } from 'zod';

const identityStepEditYupObjectSchema = yupObject({
  w9_form_url: yupString().required(),
  transportation_plate_registration: yupString().when(
    '$logisticsType',
    (
      logisticsType: (typeof CARRIER_LOGISTICS)[number],
      schema: StringSchema,
    ) => {
      return logisticsType === 'driveaway' ? schema.required() : schema;
    },
  ),
  state_motor_carrier_permit: yupString().when(
    ['$logisticsType', '$isCarrierWithoutUSDOT'],
    (
      logisticsType: (typeof CARRIER_LOGISTICS)[number],
      isCarrierWithoutUSDOT: boolean,
      schema: StringSchema,
    ) => {
      return logisticsType !== 'driveaway' && isCarrierWithoutUSDOT
        ? schema.required()
        : schema;
    },
  ),
  business_license: yupString().when(
    ['$logisticsType', '$isCarrierWithoutUSDOT'],
    (
      logisticsType: (typeof CARRIER_LOGISTICS)[number],
      isCarrierWithoutUSDOT: boolean,
      schema: StringSchema,
    ) => {
      return logisticsType === 'driveaway' && isCarrierWithoutUSDOT
        ? schema.required()
        : schema;
    },
  ),
});

export interface IdentityStepContext {
  logisticsType: null | undefined | (typeof CARRIER_LOGISTICS)[number];
  isCarrierWithoutUSDOT: boolean;
}

export type IdentityStepEditDTO = z.infer<
  ReturnType<typeof createIdentityStepEditSchema>
>;

export function createIdentityStepEditSchema({
  logisticsType,
  isCarrierWithoutUSDOT,
}: IdentityStepContext) {
  const isDriveaway = logisticsType === 'driveaway';

  return z
    .object({
      w9_form_url: z.string().catch(''),
      transportation_plate_registration: z.string().catch(''),
      state_motor_carrier_permit: z.string().catch(''),
      business_license: z.string().catch(''),
    })
    .superRefine((values, context) => {
      function addIssue(path: string) {
        context.addIssue({
          code: 'custom',
          path: [path],
          message: 'This field is required',
        });
      }

      if (!values.w9_form_url) {
        addIssue('w9_form_url');
      }

      if (isDriveaway && !values.transportation_plate_registration) {
        addIssue('transportation_plate_registration');
      }

      if (
        !isDriveaway &&
        isCarrierWithoutUSDOT &&
        !values.state_motor_carrier_permit
      ) {
        addIssue('state_motor_carrier_permit');
      }

      if (isDriveaway && isCarrierWithoutUSDOT && !values.business_license) {
        addIssue('business_license');
      }
    });
}

export const identityStepEditYupSchema =
  identityStepEditYupObjectSchema as unknown as Schema<IdentityStepEditDTO>;
